import { useDroppable } from '@dnd-kit/core';
import {
  Archive,
  Download,
  FolderPlus,
  Info,
  ListPlus,
  LogIn,
  Mail,
  MoreVertical,
  Smartphone,
  Trash2,
  Upload,
  User,
  Zap,
} from 'lucide-react';
import { BrandIcon } from '@/components/ui/BrandIcon';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { brand } from '@/lib/brand';

interface TreeViewHeaderProps {
  isDragging?: boolean;
  canCreateFolderOrList: boolean;
  showArchivedTemplates?: boolean;
  showArchivedSessions?: boolean;
  hideArchivedTemplatesToggle?: boolean;
  hideArchivedSessionsToggle?: boolean;
  hasArchivedTemplates?: boolean;
  onHeaderClick: () => void;
  onAddFolder: () => void;
  onAddTemplate: () => void;
  onExport: () => void;
  onImport: () => void;
  onToggleShowArchivedTemplates?: () => void;
  onToggleShowArchivedSessions?: () => void;
  onEmptyTrash?: () => void;
  onSignOut?: () => void;
  onSignIn?: () => void;
  onDeleteAccount?: () => void;
  onPendingInvites?: () => void;
  isAuthenticated?: boolean;
  showProfileDialog?: boolean;
  onShowProfileDialogChange?: (show: boolean) => void;
  canInstallApp?: boolean;
  onInstallApp?: () => void;
  onAbout?: () => void;
  // Subscription info
  subscriptionTier?: string;
  listCount?: number;
  maxLists?: number;
  onUpgradeClick?: () => void;
}

/**
 * Root-level header and drop zone for the main tree view.
 * Acts as a droppable target for moving folders to root level.
 * Styled to integrate with the tree structure.
 */
export function TreeViewHeader({
  isDragging = false,
  canCreateFolderOrList,
  showArchivedTemplates = false,
  showArchivedSessions = false,
  hideArchivedTemplatesToggle = false,
  hideArchivedSessionsToggle = false,
  hasArchivedTemplates = false,
  onHeaderClick,
  onAddFolder,
  onAddTemplate,
  onExport,
  onImport,
  onToggleShowArchivedTemplates,
  onToggleShowArchivedSessions,
  onEmptyTrash,
  onSignOut,
  onSignIn,
  onDeleteAccount,
  onPendingInvites,
  isAuthenticated = false,
  showProfileDialog: _showProfileDialog = false,
  onShowProfileDialogChange,
  canInstallApp = false,
  onInstallApp,
  onAbout,
  subscriptionTier = 'Free',
  listCount = 0,
  maxLists = 3,
  onUpgradeClick,
}: TreeViewHeaderProps) {
  // Droppable setup for root-level drops (always called for hooks rules)
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: 'drop-__ROOT_DROP_ZONE__',
    data: { path: '__ROOT_DROP_ZONE__' },
  });

  // Only use drag-and-drop functionality when enabled
  const effectiveSetDropRef = isDragging !== undefined ? setDropRef : undefined;
  const effectiveIsOver = isDragging !== undefined ? isOver : false;

  return (
    <header
      ref={effectiveSetDropRef}
      className={`px-3 py-3 sm:px-4 sm:py-4 border-b transition-all ${
        isDragging && effectiveIsOver
          ? 'bg-green-50 dark:bg-green-900/30 border-green-500 border-2 border-dashed'
          : isDragging
            ? 'bg-surface-secondary border-divider-primary border-2 border-dashed'
            : 'bg-surface-elevated border-divider-secondary'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onHeaderClick}
          className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity min-h-[44px]"
          aria-label={brand.headerAriaLabel}
        >
          <BrandIcon className="h-8 sm:h-9 lg:h-10" height={40} />
          <h1 className="text-xl font-bold text-content-primary sm:text-2xl lg:text-3xl">
            {brand.headerText}
          </h1>
        </button>
        <TooltipProvider>
          <div className="flex items-center gap-2">
            {canCreateFolderOrList && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      onClick={onAddFolder}
                      variant="outline"
                      size="icon"
                      aria-label="New folder"
                    >
                      <FolderPlus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New Folder</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      onClick={onAddTemplate}
                      variant="primary"
                      size="icon"
                      aria-label="New list"
                    >
                      <ListPlus className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>New List</p>
                  </TooltipContent>
                </Tooltip>
              </>
            )}
            {!isAuthenticated && onSignIn && (
              <Button
                type="button"
                onClick={onSignIn}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="icon" aria-label="More options">
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onImport}>
                  <Upload className="mr-2 h-4 w-4" />
                  Import
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onExport}>
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </DropdownMenuItem>
                {(!hideArchivedTemplatesToggle || !hideArchivedSessionsToggle) && (
                  <DropdownMenuSeparator />
                )}
                {!hideArchivedTemplatesToggle && (
                  <DropdownMenuCheckboxItem
                    checked={showArchivedTemplates}
                    onCheckedChange={onToggleShowArchivedTemplates}
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Show Archived Lists
                  </DropdownMenuCheckboxItem>
                )}
                {!hideArchivedSessionsToggle && (
                  <DropdownMenuCheckboxItem
                    checked={showArchivedSessions}
                    onCheckedChange={onToggleShowArchivedSessions}
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Show Archived Sessions
                  </DropdownMenuCheckboxItem>
                )}
                {!hideArchivedTemplatesToggle &&
                  showArchivedTemplates &&
                  hasArchivedTemplates &&
                  onEmptyTrash && (
                    <DropdownMenuItem onClick={onEmptyTrash} className="text-red-600">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Empty Trash
                    </DropdownMenuItem>
                  )}
                {(onSignOut || onSignIn || onPendingInvites || canInstallApp) && (
                  <>
                    <DropdownMenuSeparator />
                    {canInstallApp && onInstallApp && (
                      <DropdownMenuItem onClick={onInstallApp}>
                        <Smartphone className="mr-2 h-4 w-4" />
                        Install App
                      </DropdownMenuItem>
                    )}
                    {isAuthenticated && onPendingInvites && (
                      <DropdownMenuItem onClick={onPendingInvites}>
                        <Mail className="mr-2 h-4 w-4" />
                        Pending Invites
                      </DropdownMenuItem>
                    )}
                    {maxLists !== -1 && onUpgradeClick && (
                      <DropdownMenuItem onClick={onUpgradeClick}>
                        <Zap className="mr-2 h-4 w-4 text-green-600" />
                        <span className="flex-1">Upgrade Plan</span>
                        <span className="ml-4 text-xs text-content-tertiary">
                          {subscriptionTier} {listCount}/{maxLists}
                        </span>
                      </DropdownMenuItem>
                    )}
                    {isAuthenticated &&
                    onSignOut &&
                    onDeleteAccount &&
                    onShowProfileDialogChange ? (
                      <DropdownMenuItem onClick={() => onShowProfileDialogChange(true)}>
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </DropdownMenuItem>
                    ) : (
                      onSignIn && (
                        <DropdownMenuItem onClick={onSignIn}>
                          <LogIn className="mr-2 h-4 w-4" />
                          Sign In
                        </DropdownMenuItem>
                      )
                    )}
                  </>
                )}
                {onAbout && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onAbout}>
                      <Info className="mr-2 h-4 w-4" />
                      About
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TooltipProvider>
      </div>
    </header>
  );
}
